import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Trash2, ArrowRight, BookOpen } from 'lucide-react';

const SavedCoursesList = ({ courses, onDelete }) => {
  if (!courses || courses.length === 0) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="py-10 text-center text-gray-500" data-testid="courses-empty">
          <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          You haven't created any courses yet. Generate one above to get started.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {courses.map((c) => {
        const progress =
          c.lesson_count > 0
            ? Math.round((c.completed / c.lesson_count) * 100)
            : 0;
        return (
          <Card
            key={c.id}
            className="bg-white border-gray-200 hover:shadow-lg transition-shadow"
            data-testid={`course-card-${c.id}`}
          >
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <Badge
                  variant="outline"
                  className="text-[10px] uppercase tracking-wider border-purple-200 text-purple-700"
                >
                  {c.mode === 'quick' ? 'Quick search' : 'Full course'}
                </Badge>
                {c.level && (
                  <Badge className="bg-gray-100 text-gray-700 border border-gray-200 text-[10px]">
                    {c.level}
                  </Badge>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 line-clamp-2">
                {c.title || c.topic}
              </h3>
              <p className="text-xs text-gray-500">
                {c.mode === 'full'
                  ? `${c.module_count} modules · ${c.lesson_count} lessons`
                  : `Topic: ${c.topic}`}
              </p>
              {c.mode === 'full' && c.lesson_count > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Progress</span>
                    <span>
                      {c.completed} / {c.lesson_count} · {progress}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-fuchsia-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <Link to={`/courses/${c.id}`}>
                  <Button
                    size="sm"
                    className="bg-gray-900 hover:bg-black text-white"
                    data-testid={`course-open-${c.id}`}
                  >
                    Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(c.id)}
                  className="text-gray-400 hover:text-red-500"
                  data-testid={`course-delete-${c.id}`}
                  title="Delete course"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SavedCoursesList;
